import Box from "@/common/components/Box";
import ListHeader from "@/common/components/ListHeader";
import { Receipt } from "lucide-react";
import { ConsumptionValidationSchema, createSchema } from "../../schemas/create.schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, SubmitHandler, useFieldArray, useForm } from "react-hook-form";
import { useState } from "react";
import TabInformation from "../../components/TabInformation";
import TabResume from "../../components/TabResume";
import ModalSuccess from "../../components/ModalSuccess";
import useCreateConsumption from "../../hooks/useCreateConsumption";
import { ConsumptionData } from "../../types/consumption.type";
import { ModalErrorMessage } from "@/common/components/ModalErrorMessage";
import { ErrorResponse } from "@/common/types/errorResponse";

const Create = () => {
  const [activeTab, setActiveTab] = useState(0);

  const [modalSuccessData, setModalSuccessData] = useState<ConsumptionData | boolean>(false);
  const [modalErrorData, setModalErrorData] = useState<ErrorResponse | boolean>(false);

  const useFormMethods = useForm<ConsumptionValidationSchema>({
    resolver: zodResolver(createSchema)
  });

  const {
    register,
    handleSubmit,
    control,
    watch,
    trigger,
    reset: resetForm,
    formState: { errors }
  } = useFormMethods;

  const { fields: lineItemsFields, remove: removeLineItem } = useFieldArray({
    control,
    name: "line_items_attributes",
  });

  const { mutate: createConsumption, isPending } = useCreateConsumption({
    onSuccess: (response) => {
      setModalSuccessData(response.data.data);
    },
    onError: (response) => {
      setModalErrorData(response);
    },
  });

  const onSubmit: SubmitHandler<ConsumptionValidationSchema> = async (data) => {
    const isValid = await trigger();

    if (!isValid)
      return;

    return createConsumption(data);
  };

  const clearForm = () => {
    resetForm();
    removeLineItem([...Array(lineItemsFields.length).keys()]);
    setActiveTab(0);
  };

  const handleTabChange = async (tabIndex: number) => {
    if (tabIndex > activeTab) {
      const isValid = await trigger([
        "card_identifier",
        "card_id",
        "establishment_branch_id",
        "buyer_id",
        "line_items_attributes",
      ]);

      if (!isValid)
        return;
    }

    setActiveTab(tabIndex);
  };

  return (
    <FormProvider {...useFormMethods}>
      <form className="flex flex-col gap-6 h-full" onSubmit={handleSubmit(onSubmit)}>
        <ListHeader
          Icon={<Receipt className="w-10 h-10" strokeWidth={1.5} />}
          title="Consumo"
        />

        <Box className="flex flex-col gap-6 grow h-[calc((100vh-276px)/2)]">
          {activeTab === 0 && (
            <TabInformation
              key="tabInformation"
              activeTab={activeTab}
              handleTabChange={handleTabChange}
              clearForm={clearForm}
            />
          )}

          {activeTab === 1 && (
            <TabResume
              key="tabResume"
              activeTab={activeTab}
              handleTabChange={handleTabChange}
              watch={watch}
              register={register}
              errors={errors}
              isPending={isPending}
            />
          )}
        </Box>
      </form>

      <ModalSuccess
        data={modalSuccessData as ConsumptionData}
        setData={setModalSuccessData as React.Dispatch<React.SetStateAction<boolean>>}
        clearForm={clearForm}
      />

      <ModalErrorMessage
        data={modalErrorData as ErrorResponse}
        setData={setModalErrorData as React.Dispatch<React.SetStateAction<boolean>>}
        title="Transação não concluída"
        message="Algo deu errado e sua transação não foi concluída."
      />
    </FormProvider>
  );
};

export default Create;
